/**
 * Módulo 04 — as integrações com o ERP (0104), na tela: os tipos das respostas,
 * os rótulos e os retornos de cada gravação (R04). Funções puras, as mesmas na
 * mesa e no celular, testadas sem navegador.
 *
 * Nenhum ERP é nomeado aqui: o sistema é dado da empresa (ADR-006).
 */
import { formatarCompetencia, formatarDia, formatarInteiro, formatarReais } from '@/lib/formato';
import type { RetornoDeclarado } from '@/lib/retorno';

/** O retorno sem o título: o título é de quem declara a gravação. */
export type DetalheDoRetorno = Omit<RetornoDeclarado, 'titulo'>;

/* ------------------------------------------------------------------ tipos */

export type Entidade =
  | 'fornecedor' | 'cliente' | 'centro_custo' | 'conta_gerencial' | 'pedido_de_compra' | 'titulo_a_pagar'
  | 'titulo_a_receber' | 'baixa' | 'lancamento_gerencial' | 'nota_de_entrada' | 'nota_de_saida'
  | 'lancamento_contabil' | 'apuracao_de_imposto' | 'hora_reconhecida' | 'leitura_de_telemetria';

export type CadastroDeEntrada = 'fornecedor' | 'cliente' | 'centro_custo';
export const CADASTROS_DE_ENTRADA: readonly CadastroDeEntrada[] = ['fornecedor', 'cliente', 'centro_custo'];

/** As contas que entram do mestre de fora (0108): títulos, baixas e notas como referência. */
export type ContaDeEntrada = 'titulo_a_pagar' | 'titulo_a_receber' | 'baixa' | 'nota_de_entrada' | 'nota_de_saida';
export const CONTAS_DE_ENTRADA: readonly ContaDeEntrada[] = ['titulo_a_pagar', 'titulo_a_receber', 'baixa', 'nota_de_entrada', 'nota_de_saida'];

/** Tudo o que se importa por arquivo na versão 1. */
export type EntidadeDeImportacao = CadastroDeEntrada | ContaDeEntrada;
export const ENTIDADES_DE_IMPORTACAO: readonly EntidadeDeImportacao[] = [...CADASTROS_DE_ENTRADA, ...CONTAS_DE_ENTRADA];

/** O que sai da plataforma (0108): os lançamentos do mês, o pedido e os cadastros quando ela é a mestre. */
export type EntidadeDeSaida = 'lancamento_gerencial' | 'pedido_de_compra' | 'fornecedor' | 'cliente' | 'centro_custo';
export const ENTIDADES_DE_SAIDA: readonly EntidadeDeSaida[] = ['lancamento_gerencial', 'pedido_de_compra', 'fornecedor', 'cliente', 'centro_custo'];
export const ROTULO_DA_SAIDA: Readonly<Record<EntidadeDeSaida, string>> = {
  lancamento_gerencial: 'Lançamentos gerenciais do mês',
  pedido_de_compra: 'Pedidos de compra',
  fornecedor: 'Fornecedores',
  cliente: 'Clientes',
  centro_custo: 'Centros de custo',
};

/** O mestre de hoje de cada entidade com troca: entrada (o mestre de fora) e saída (o destino). */
export function mestresDeHoje(linhas: readonly LinhaDaMatriz[]): {
  readonly entrada: Readonly<Partial<Record<Entidade, string>>>;
  readonly saida: Readonly<Partial<Record<Entidade, string>>>;
} {
  const entrada: Partial<Record<Entidade, string>> = {};
  const saida: Partial<Record<Entidade, string>> = {};
  for (const l of linhas) {
    if (!l.vigente || l.situacao !== 'aprovada') continue;
    if (l.direcao === 'entrada' && l.sistemaMestre) entrada[l.entidade] = l.sistemaMestre;
    if (l.direcao === 'saida' && l.sistemaParceiro) saida[l.entidade] = l.sistemaParceiro;
  }
  return { entrada, saida };
}

export type Formato = 'arquivo_csv' | 'arquivo_json' | 'api' | 'manual';
export type Direcao = 'entrada' | 'saida' | 'nenhuma';
export type SituacaoDaLinha = 'proposta' | 'aprovada' | 'recusada' | 'encerrada';
export type SituacaoDoLote = 'estagio' | 'promovido' | 'descartado' | 'gerado' | 'confirmado' | 'divergente' | 'rejeitado';
export type TipoDeDivergencia = 'so_na_plataforma' | 'so_no_externo' | 'valor_diferente' | 'total_diferente';
export type Resolucao = 'corrigida_na_plataforma' | 'corrigida_no_externo' | 'diferenca_aceita' | 'reenviada_em_outro_lote';

export interface DescricaoDaEntidade {
  readonly entidade: Entidade;
  readonly rotulo: string;
  readonly plural: string;
  readonly fiscal: boolean;
  readonly leitores: readonly ('entrada' | 'saida')[];
  readonly situacao: string;
}

export interface SistemaExterno {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly tipo: string;
  readonly demonstracao: boolean;
  readonly observacao: string;
  readonly ativo: boolean;
}

export interface LinhaDaMatriz {
  readonly id: string;
  readonly entidade: Entidade;
  readonly direcao: Direcao;
  readonly formato: Formato;
  readonly frequenciaDias: number;
  readonly contingencia: string;
  readonly inicio: string;
  readonly fim: string | null;
  readonly situacao: SituacaoDaLinha;
  readonly sistemaMestreId: string | null;
  readonly sistemaMestre: string | null;
  readonly sistemaParceiroId: string | null;
  readonly sistemaParceiro: string | null;
  readonly propostaPorId: string;
  readonly propostaPor: string | null;
  readonly propostaEm: string;
  readonly decididaPor: string | null;
  readonly decididaEm: string | null;
  readonly nota: string | null;
  readonly encerradaPor: string | null;
  readonly encerradaEm: string | null;
  readonly motivoEncerramento: string | null;
  readonly vigente: boolean;
  readonly atrasada: boolean | null;
  readonly diasSemTroca: number | null;
  readonly ultimoLoteEm: string | null;
}

export interface RespostaDaMatriz {
  readonly linhas: readonly LinhaDaMatriz[];
  readonly entidades: readonly DescricaoDaEntidade[];
  readonly fiscal: string;
  readonly hoje: string;
}

export interface Resumo {
  readonly vigentes: number;
  readonly propostas: number;
  readonly emConferencia: number;
  readonly divergenciasAbertas: number;
  readonly trocasAtrasadas: number;
  readonly sistemas: number;
}

export interface Lote {
  readonly id: string;
  readonly entidade: Entidade;
  readonly direcao: 'entrada' | 'saida';
  readonly formato: 'arquivo_csv' | 'arquivo_json' | 'api';
  readonly nomeArquivo: string;
  readonly dia: string;
  readonly competencia: string | null;
  readonly revisao: number | null;
  readonly linhas: number;
  readonly linhasValidas: number;
  readonly totais: Readonly<Record<string, unknown>>;
  readonly situacao: SituacaoDoLote;
  readonly motivo: string | null;
  readonly motivoDescarte: string | null;
  readonly criadoEm: string;
  readonly criadoPor: string | null;
  readonly decididoEm: string | null;
  readonly decididoPor: string | null;
  readonly sistema: string;
  readonly demonstracao: boolean;
  readonly divergenciasAbertas: number;
}

export interface LinhaDoLote {
  readonly numeroLinha: number;
  readonly bruto: string;
  readonly chave: string | null;
  readonly dados: Readonly<Record<string, unknown>> | null;
  readonly acao: string | null;
  readonly revisao: number | null;
  readonly valor: number | null;
  readonly erro: string | null;
}

export interface DetalheDoLote {
  readonly lote: Lote;
  readonly contagem: Readonly<Record<string, number>>;
  readonly linhas: readonly LinhaDoLote[];
  readonly filtro: FiltroDaLinha;
  readonly pagina: number;
  readonly porPagina: number;
  readonly totalDoFiltro: number;
  readonly retornos?: readonly { readonly id: string; readonly nomeArquivo: string; readonly linhas: number; readonly resumo: Readonly<Record<string, unknown>>; readonly recebidoEm: string; readonly recebidoPor: string | null }[];
  readonly semLicenca?: string;
}

export interface Correspondencia {
  readonly id: string;
  readonly sistemaId: string;
  readonly sistema: string;
  readonly entidade: CadastroDeEntrada;
  readonly chaveExterna: string;
  readonly registroId: string;
  readonly registroCodigo: string | null;
  readonly registroNome: string | null;
  readonly origem: 'lote' | 'manual';
  readonly loteId: string | null;
  readonly criadoEm: string;
}

export interface Divergencia {
  readonly id: string;
  readonly loteId: string;
  readonly competencia: string | null;
  readonly revisao: number | null;
  readonly sistema: string;
  readonly tipo: TipoDeDivergencia;
  readonly chaveDeTroca: string | null;
  readonly competenciaDoTotal: string | null;
  readonly contaContabil: string | null;
  readonly valorPlataforma: number | null;
  readonly valorExterno: number | null;
  readonly diferenca: number;
  readonly mensagemExterno: string | null;
  readonly situacao: 'aberta' | 'resolvida';
  readonly responsavel: string | null;
  readonly responsavelId: string | null;
  readonly justificativa: string | null;
  readonly resolucao: string | null;
  readonly resolvidaPor: string | null;
  readonly resolvidaEm: string | null;
  readonly linha: { readonly conta?: string | null; readonly equipamento?: string | null; readonly contrato?: string | null; readonly grupo?: string | null } | null;
}

export interface Pagina<T> {
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly itens: readonly T[];
}

/* ----------------------------------------------------------------- rótulos */

export const ROTULO_DA_ENTIDADE: Readonly<Record<Entidade, string>> = {
  fornecedor: 'Fornecedor',
  cliente: 'Cliente',
  centro_custo: 'Centro de custo',
  conta_gerencial: 'Conta gerencial',
  pedido_de_compra: 'Pedido de compra',
  titulo_a_pagar: 'Título a pagar',
  titulo_a_receber: 'Título a receber',
  baixa: 'Baixa de título',
  lancamento_gerencial: 'Lançamento gerencial',
  nota_de_entrada: 'Nota de entrada',
  nota_de_saida: 'Nota de saída',
  lancamento_contabil: 'Lançamento contábil',
  apuracao_de_imposto: 'Apuração de imposto',
  hora_reconhecida: 'Hora reconhecida',
  leitura_de_telemetria: 'Leitura de telemetria',
};

export const ROTULO_DO_FORMATO: Readonly<Record<Formato, string>> = {
  arquivo_csv: 'Arquivo CSV',
  arquivo_json: 'Arquivo JSON',
  api: 'API',
  manual: 'Manual',
};

export const ROTULO_DO_TIPO_DE_SISTEMA: Readonly<Record<string, string>> = {
  erp: 'ERP',
  contabilidade: 'Contabilidade',
  banco: 'Banco',
  cliente: 'Sistema do cliente',
  telemetria: 'Telemetria',
  posto: 'Posto',
  outro: 'Outro',
};

export type TomDoSelo = 'sucesso' | 'atencao' | 'erro' | 'informacao' | 'neutro';

/** A situação da linha da matriz, em selo (p.24): nunca um dos seis status da máquina. */
export const SITUACAO_DA_LINHA: Readonly<Record<SituacaoDaLinha, { readonly rotulo: string; readonly tom: TomDoSelo }>> = {
  proposta: { rotulo: 'Proposta', tom: 'atencao' },
  aprovada: { rotulo: 'Aprovada', tom: 'sucesso' },
  recusada: { rotulo: 'Recusada', tom: 'neutro' },
  encerrada: { rotulo: 'Encerrada', tom: 'neutro' },
};

export const SITUACAO_DO_LOTE: Readonly<Record<SituacaoDoLote, { readonly rotulo: string; readonly tom: TomDoSelo }>> = {
  estagio: { rotulo: 'Em conferência', tom: 'atencao' },
  promovido: { rotulo: 'Promovido', tom: 'sucesso' },
  descartado: { rotulo: 'Descartado', tom: 'neutro' },
  gerado: { rotulo: 'Aguardando retorno', tom: 'informacao' },
  confirmado: { rotulo: 'Confirmado', tom: 'sucesso' },
  divergente: { rotulo: 'Com divergência', tom: 'atencao' },
  rejeitado: { rotulo: 'Rejeitado', tom: 'erro' },
};

export const ROTULO_DA_ACAO: Readonly<Record<string, string>> = {
  criar: 'Criar',
  atualizar: 'Atualizar',
  vincular: 'Vincular',
  sem_mudanca: 'Sem mudança',
  lancamento: 'Lançamento',
  estorno: 'Estorno',
  erro: 'Com erro',
};

export type FiltroDaLinha = 'todas' | 'erro' | 'criar' | 'atualizar' | 'vincular' | 'sem_mudanca' | 'lancamento' | 'estorno';
export const FILTROS_DA_ENTRADA: readonly FiltroDaLinha[] = ['todas', 'erro', 'criar', 'atualizar', 'vincular', 'sem_mudanca'];
export const FILTROS_DA_SAIDA: readonly FiltroDaLinha[] = ['todas', 'lancamento', 'estorno'];
/** A saída de registro (0108): novo, alterado e cancelado. */
export const FILTROS_DA_SAIDA_DE_REGISTRO: readonly FiltroDaLinha[] = ['todas', 'criar', 'atualizar', 'estorno'];

export const ROTULO_DO_TIPO_DE_DIVERGENCIA: Readonly<Record<TipoDeDivergencia, string>> = {
  so_na_plataforma: 'Só na plataforma',
  so_no_externo: 'Só no sistema de fora',
  valor_diferente: 'Valor diferente',
  total_diferente: 'Total diferente',
};

export const ROTULO_DA_RESOLUCAO: Readonly<Record<string, string>> = {
  corrigida_na_plataforma: 'Corrigida na plataforma',
  corrigida_no_externo: 'Corrigida no sistema de fora',
  diferenca_aceita: 'Diferença aceita',
  reenviada_em_outro_lote: 'Reenviada em outro lote',
  sem_divergencia_no_novo_retorno: 'Sem divergência no retorno seguinte',
};

export const RESOLUCOES: readonly Resolucao[] = ['corrigida_na_plataforma', 'corrigida_no_externo', 'diferenca_aceita', 'reenviada_em_outro_lote'];

export const ROTULO_DO_GRUPO: Readonly<Record<string, string>> = {
  receita: 'Receita',
  custo_direto: 'Custo direto',
  custo_indireto: 'Custo indireto',
  depreciacao: 'Depreciação',
};

/* ---------------------------------------------------------------- frases */

/** O formato no meio da frase: "arquivo CSV", "API", "lançamento manual". */
const FORMATO_NA_FRASE: Readonly<Record<Formato, string>> = {
  arquivo_csv: 'arquivo CSV',
  arquivo_json: 'arquivo JSON',
  api: 'API',
  manual: 'lançamento manual',
};

/** "Mestre: ERP da empresa · entra por arquivo CSV, a cada 7 dias". */
export function fraseDaLinha(l: Pick<LinhaDaMatriz, 'sistemaMestre' | 'sistemaParceiro' | 'direcao' | 'formato' | 'frequenciaDias'>): string {
  const mestre = l.sistemaMestre ?? 'a plataforma';
  if (l.direcao === 'nenhuma') return `Mestre: ${mestre} · sem troca`;
  const quem = l.direcao === 'entrada' ? 'entra' : `sai para ${l.sistemaParceiro ?? ''}`;
  return `Mestre: ${mestre} · ${quem} por ${FORMATO_NA_FRASE[l.formato]}, a cada ${dias(l.frequenciaDias)}`;
}

export const dias = (n: number) => (n === 1 ? '1 dia' : `${formatarInteiro(n)} dias`);

/** "desde 07/10/2026" ou "de 01/09/2026 a 06/10/2026". */
export function vigenciaEscrita(l: Pick<LinhaDaMatriz, 'inicio' | 'fim'>): string {
  return l.fim ? `de ${formatarDia(l.inicio)} a ${formatarDia(l.fim)}` : `desde ${formatarDia(l.inicio)}`;
}

/** A troca da linha vigente: em dia, atrasada, ou sem lote ainda. */
export function trocaEscrita(l: Pick<LinhaDaMatriz, 'atrasada' | 'diasSemTroca' | 'ultimoLoteEm' | 'frequenciaDias'>): { readonly texto: string; readonly tom: TomDoSelo } | null {
  if (l.atrasada === null) return null;
  const desde = l.ultimoLoteEm ? `último lote há ${dias(l.diasSemTroca ?? 0)}` : `nenhum lote em ${dias(l.diasSemTroca ?? 0)}`;
  return l.atrasada ? { texto: `Troca atrasada · ${desde}`, tom: 'atencao' } : { texto: `Troca em dia · ${desde}`, tom: 'sucesso' };
}

/** O que a linha do lote diz: a ação, as mudanças ou o erro. */
export function oQueALinhaDiz(l: LinhaDoLote, entidade: Entidade = 'fornecedor'): string {
  if (l.erro) return l.erro;
  const mudancas = Array.isArray(l.dados?.['mudancas']) ? (l.dados['mudancas'] as string[]) : [];
  if (mudancas.length > 0) return mudancas.join(' · ');
  const conta = (CONTAS_DE_ENTRADA as readonly string[]).includes(entidade);
  if (l.acao === 'criar') return conta ? `Cria ${ARTIGO_DA_CONTA[entidade as ContaDeEntrada]} na plataforma, com a chave do mestre.` : 'Cria o cadastro na plataforma e a correspondência.';
  if (l.acao === 'vincular') return conta ? 'Já existia, lançado em contingência: o lote casa, sem duplicar.' : 'O cadastro já existia: vincula, sem duplicar.';
  if (l.acao === 'sem_mudanca') return 'Igual ao que a plataforma tem.';
  if (l.acao === 'atualizar') return 'Sai a revisão seguinte: o registro mudou desde a última saída.';
  return '';
}

const ARTIGO_DA_CONTA: Readonly<Record<ContaDeEntrada, string>> = {
  titulo_a_pagar: 'o título a pagar',
  titulo_a_receber: 'o título a receber',
  baixa: 'a baixa',
  nota_de_entrada: 'a referência da nota',
  nota_de_saida: 'a referência da nota',
};

/** O título da linha do lote, pela entidade: o cadastro, a conta, o pedido ou a conta gerencial. */
export function tituloDaLinha(l: LinhaDoLote, direcao: 'entrada' | 'saida', entidade: Entidade = direcao === 'saida' ? 'lancamento_gerencial' : 'fornecedor'): string {
  const d = l.dados ?? {};
  const texto = (v: unknown) => (typeof v === 'string' && v !== '' ? v : null);
  if (direcao === 'saida') {
    if (entidade === 'pedido_de_compra') return `Pedido ${texto(d['numero']) ?? ''} · item ${String(d['item'] ?? '')} · ${texto(d['produto']) ?? ''}`;
    if (entidade !== 'lancamento_gerencial') return `${texto(d['codigo']) ?? ''} · ${texto(d['nome']) ?? ''}`;
    const conta = texto(d['conta']) ?? 'sem conta';
    const quem = [d['equipamento'], d['contrato']].filter((x): x is string => typeof x === 'string' && x !== '').join(' · ');
    return `Conta ${conta}${quem ? ` · ${quem}` : ''}`;
  }
  const campos = (d['campos'] ?? null) as Record<string, unknown> | null;
  if (!campos) return `Linha ${formatarInteiro(l.numeroLinha)}`;
  if (entidade === 'baixa') return `Baixa ${texto(campos['chave_externa']) ?? ''} · título ${texto(campos['titulo']) ?? ''}`;
  if (entidade === 'titulo_a_pagar' || entidade === 'titulo_a_receber' || entidade === 'nota_de_entrada' || entidade === 'nota_de_saida') {
    return `${texto(campos['numero']) ?? ''} · ${texto(campos['fornecedor']) ?? texto(campos['cliente']) ?? ''}`;
  }
  return `${texto(campos['codigo']) ?? ''} · ${texto(campos['nome']) ?? ''}`;
}

/** As competências que podem sair: os doze meses terminados antes do corrente. */
export function competenciasFechadas(hoje: string, quantas = 12): string[] {
  const [ano, mes] = hoje.split('-').map(Number) as [number, number];
  const lista: string[] = [];
  for (let i = 1; i <= quantas; i++) {
    const d = new Date(Date.UTC(ano, mes - 1 - i, 1));
    lista.push(`${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return lista;
}

/** A diferença com o sinal escrito: "+R$ 1,00" / "−R$ 0,02". */
export function diferencaEscrita(v: number): string {
  if (v === 0) return formatarReais(0);
  return `${v > 0 ? '+' : '−'}${formatarReais(Math.abs(v))}`;
}

/** Onde a divergência mora: a conta da linha, ou o total da conta contábil no mês. */
export function alvoDaDivergencia(d: Divergencia): string {
  if (d.tipo === 'total_diferente') {
    return `Total da conta contábil ${d.contaContabil ?? 'sem conta'} em ${d.competenciaDoTotal ? formatarCompetencia(d.competenciaDoTotal) : ''}`;
  }
  const l = d.linha ?? {};
  const quem = [l.equipamento, l.contrato].filter((x): x is string => typeof x === 'string' && x !== '').join(' · ');
  return `Conta ${l.conta ?? 'sem conta'}${quem ? ` · ${quem}` : ''}`;
}

/* ----------------------------------------------------- os arquivos lidos */

/** O limite do arquivo na tela: o mesmo do servidor (8 MB). */
export const TAMANHO_MAXIMO = 8 * 1024 * 1024;

/* --------------------------------------------- os retornos (R04, p.32) */

export function retornoDoSistema(r: { readonly codigo: string; readonly nome: string; readonly tipo: string }): DetalheDoRetorno {
  return { alvo: { codigo: r.codigo, nome: r.nome }, fatos: [ROTULO_DO_TIPO_DE_SISTEMA[r.tipo] ?? r.tipo], efeito: 'Já pode ser mestre ou parceiro de uma linha da matriz' };
}

export function retornoDaProposta(r: { readonly entidade: Entidade; readonly direcao: Direcao; readonly inicio: string }): DetalheDoRetorno {
  return {
    alvo: { nome: ROTULO_DA_ENTIDADE[r.entidade] },
    fatos: [r.direcao === 'entrada' ? 'entrada do mestre' : r.direcao === 'saida' ? 'saída da plataforma' : 'sem troca', `a partir de ${formatarDia(r.inicio)}`],
    efeito: 'Outra pessoa com a licença de aprovar a matriz decide; quem propõe não aprova',
  };
}

export function retornoDaDecisao(r: { readonly entidade: Entidade; readonly decisao: 'aprovada' | 'recusada'; readonly inicio: string }, nota: string): DetalheDoRetorno {
  return {
    alvo: { nome: ROTULO_DA_ENTIDADE[r.entidade] },
    fatos: [r.decisao === 'aprovada' ? `vale desde ${formatarDia(r.inicio)}` : 'a proposta não vale', `nota: ${nota.trim()}`],
    efeito: { rotulo: 'Situação da linha', antes: 'Proposta', depois: r.decisao === 'aprovada' ? 'Aprovada' : 'Recusada' },
  };
}

export function retornoDoEncerramento(r: { readonly entidade: Entidade; readonly ultimoDia: string }, motivo: string): DetalheDoRetorno {
  return {
    alvo: { nome: ROTULO_DA_ENTIDADE[r.entidade] },
    fatos: [`último dia ${formatarDia(r.ultimoDia)}`, `motivo: ${motivo.trim()}`],
    efeito: { rotulo: 'Situação da linha', antes: 'Aprovada', depois: 'Encerrada' },
  };
}

export interface RespostaDaImportacao {
  readonly loteId: string;
  readonly entidade: EntidadeDeImportacao;
  readonly sistema?: string | null;
  readonly linhas: number;
  readonly linhasValidas: number;
  readonly acoes: Readonly<Record<string, number>>;
  readonly repetido?: boolean;
}

export function retornoDaImportacao(r: RespostaDaImportacao, nomeArquivo: string): DetalheDoRetorno {
  const a = r.acoes;
  const n = (k: string) => Number(a[k] ?? 0);
  return {
    alvo: { nome: nomeArquivo },
    fatos: [
      `${formatarInteiro(r.linhasValidas)} de ${formatarInteiro(r.linhas)} linhas válidas`,
      `${formatarInteiro(n('criar'))} a criar, ${formatarInteiro(n('atualizar'))} a atualizar, ${formatarInteiro(n('vincular'))} a vincular`,
      n('sem_mudanca') > 0 ? `${formatarInteiro(n('sem_mudanca'))} sem mudança` : null,
    ],
    efeito: r.repetido ? 'Mesma chave de idempotência: é o lote que já existia' : 'Nada vale antes de você conferir e promover o lote',
    ver: `/cadastros/integracoes/lotes/${r.loteId}`,
  };
}

export interface RespostaDaPromocao {
  readonly loteId: string;
  readonly entidade: EntidadeDeImportacao;
  readonly contingenciasCasadas?: number;
  readonly sistema: string;
  readonly criados: number;
  readonly atualizados: number;
  readonly vinculados: number;
  readonly semMudanca: number;
  readonly deFora: number;
}

export function retornoDaPromocao(r: RespostaDaPromocao, nomeArquivo: string): DetalheDoRetorno {
  return {
    alvo: { nome: nomeArquivo },
    fatos: [
      `${formatarInteiro(r.criados)} criados, ${formatarInteiro(r.atualizados)} atualizados, ${formatarInteiro(r.vinculados)} vinculados`,
      r.semMudanca > 0 ? `${formatarInteiro(r.semMudanca)} sem mudança` : null,
      r.deFora > 0 ? `${formatarInteiro(r.deFora)} linhas ficaram de fora` : null,
      (r.contingenciasCasadas ?? 0) > 0 ? `${formatarInteiro(r.contingenciasCasadas ?? 0)} lançadas em contingência casadas` : null,
    ],
    efeito: { rotulo: 'Situação do lote', antes: 'Em conferência', depois: 'Promovido' },
  };
}

export function retornoDoDescarte(r: { readonly nomeArquivo: string }, motivo: string, saida: boolean): DetalheDoRetorno {
  return {
    alvo: { nome: r.nomeArquivo },
    fatos: [`motivo: ${motivo.trim()}`],
    efeito: { rotulo: 'Situação do lote', antes: saida ? 'Aguardando retorno' : 'Em conferência', depois: 'Descartado' },
  };
}

export interface RespostaDaSaida {
  readonly loteId: string;
  readonly competencia: string;
  readonly revisao: number;
  readonly semMudanca?: boolean;
  readonly nomeArquivo?: string;
  readonly linhas?: number;
  readonly porGrupo?: Readonly<Record<string, number>>;
  readonly resumo?: { readonly novas: number; readonly alteradas: number; readonly iguais: number; readonly estornadas: number };
  readonly semContaContabil?: number;
}

export function tituloDaSaida(r: RespostaDaSaida): string {
  return r.semMudanca ? 'Saída do mês sem mudança' : 'Saída do mês gerada';
}

export function retornoDaSaida(r: RespostaDaSaida): DetalheDoRetorno {
  if (r.semMudanca) {
    return {
      tom: 'ressalva',
      alvo: { nome: `Competência ${formatarCompetencia(r.competencia)}` },
      fatos: [`revisão ${formatarInteiro(r.revisao)}`],
      efeito: 'Nada mudou desde a última saída: nenhum lote novo',
      ver: `/cadastros/integracoes/lotes/${r.loteId}`,
    };
  }
  const resumo = r.resumo;
  return {
    ...((r.semContaContabil ?? 0) > 0 ? { tom: 'ressalva' as const } : {}),
    alvo: { codigo: r.nomeArquivo ?? '', nome: `Competência ${formatarCompetencia(r.competencia)}` },
    fatos: [
      `${formatarInteiro(r.linhas ?? 0)} linhas, revisão ${formatarInteiro(r.revisao)}`,
      r.porGrupo ? `receita ${formatarReais(r.porGrupo['receita'] ?? 0)}` : null,
      resumo && r.revisao > 1 ? `${formatarInteiro(resumo.alteradas)} alteradas, ${formatarInteiro(resumo.estornadas)} estornadas` : null,
    ],
    efeito: (r.semContaContabil ?? 0) > 0
      ? `${formatarInteiro(r.semContaContabil ?? 0)} linhas sem conta contábil: complete o de-para no plano de contas`
      : 'Baixe o arquivo e envie ao sistema de fora; o retorno concilia',
    ver: `/cadastros/integracoes/lotes/${r.loteId}`,
  };
}

export interface RespostaDoRetorno {
  readonly correspondenciasNovas?: number;
  readonly loteId: string;
  readonly competencia: string | null;
  readonly revisao: number | null;
  readonly situacao: 'confirmado' | 'divergente' | 'rejeitado';
  readonly aceitas: number;
  readonly rejeitadas: number;
  readonly divergenciasAbertas: number;
}

export function tituloDoRetorno(r: RespostaDoRetorno): string {
  return r.situacao === 'confirmado' ? 'Retorno conferido sem divergência' : r.situacao === 'rejeitado' ? 'Retorno rejeitou a saída' : 'Retorno conferido com divergência';
}

export function retornoDoRetorno(r: RespostaDoRetorno, nomeArquivo: string): DetalheDoRetorno {
  return {
    ...(r.situacao === 'confirmado' ? {} : { tom: 'ressalva' as const }),
    alvo: { codigo: nomeArquivo, nome: r.competencia ? `Competência ${formatarCompetencia(r.competencia)}` : '' },
    fatos: [
      `${formatarInteiro(r.aceitas)} aceitas, ${formatarInteiro(r.rejeitadas)} rejeitadas`,
      `${formatarInteiro(r.divergenciasAbertas)} divergências abertas`,
      (r.correspondenciasNovas ?? 0) > 0 ? `${formatarInteiro(r.correspondenciasNovas ?? 0)} chaves do sistema de fora na correspondência` : null,
    ],
    efeito: { rotulo: 'Situação da saída', antes: 'Aguardando retorno', depois: SITUACAO_DO_LOTE[r.situacao].rotulo },
    ver: '/cadastros/integracoes/divergencias',
  };
}

export function retornoDaCorrespondencia(r: { readonly chaveExterna: string; readonly entidade: CadastroDeEntrada; readonly registro: { readonly codigo: string; readonly nome: string } | null }): DetalheDoRetorno {
  return {
    alvo: { codigo: r.chaveExterna, nome: ROTULO_DA_ENTIDADE[r.entidade] },
    fatos: [r.registro ? `${r.registro.codigo} · ${r.registro.nome}` : null],
    efeito: 'O próximo arquivo com esta chave atualiza este cadastro, sem criar outro',
  };
}

export function retornoDaTroca(r: { readonly chaveExterna: string; readonly antes: string | null; readonly depois: { readonly codigo: string } | null }, motivo: string): DetalheDoRetorno {
  return {
    alvo: { codigo: r.chaveExterna },
    fatos: [`motivo: ${motivo.trim()}`],
    efeito: { rotulo: 'Cadastro da chave', antes: r.antes ?? '—', depois: r.depois?.codigo ?? '—' },
  };
}

export function retornoDaResolucao(r: { readonly tipo: TipoDeDivergencia; readonly diferenca: number; readonly resolucao: string }, justificativa: string): DetalheDoRetorno {
  return {
    alvo: { nome: ROTULO_DO_TIPO_DE_DIVERGENCIA[r.tipo] },
    fatos: [`diferença ${diferencaEscrita(r.diferenca)}`, ROTULO_DA_RESOLUCAO[r.resolucao] ?? r.resolucao, `justificativa: ${justificativa.trim()}`],
    efeito: { rotulo: 'Situação', antes: 'Aberta', depois: 'Resolvida' },
  };
}

export function retornoDoResponsavel(r: { readonly tipo: TipoDeDivergencia }, nome: string): DetalheDoRetorno {
  return { alvo: { nome: ROTULO_DO_TIPO_DE_DIVERGENCIA[r.tipo] }, fatos: [`responsável: ${nome}`], efeito: 'Ela aparece na sua fila' };
}

export function retornoDaTolerancia(r: { readonly toleranciaReais: number; readonly antes: number }, motivo: string): DetalheDoRetorno {
  return {
    alvo: { nome: 'Tolerância da troca' },
    fatos: [`motivo: ${motivo.trim()}`],
    efeito: { rotulo: 'Tolerância', antes: formatarReais(r.antes), depois: formatarReais(r.toleranciaReais) },
  };
}

/* ---------------------------------------------------------- o endereço */

export const ABAS_DAS_INTEGRACOES = [
  { para: '/cadastros/integracoes', rotulo: 'Matriz' },
  { para: '/cadastros/integracoes/lotes', rotulo: 'Lotes' },
  { para: '/cadastros/integracoes/titulos', rotulo: 'Títulos e notas' },
  { para: '/cadastros/integracoes/correspondencias', rotulo: 'Correspondências' },
  { para: '/cadastros/integracoes/divergencias', rotulo: 'Divergências' },
] as const;

/** `?filtro=erro&pagina=2` da página do lote (p.16: o estado vai para o endereço). */
export function validarBuscaDoLote(busca: Record<string, unknown>): { readonly filtro?: FiltroDaLinha; readonly pagina?: number } {
  const filtros: readonly string[] = [...FILTROS_DA_ENTRADA, ...FILTROS_DA_SAIDA, ...FILTROS_DA_SAIDA_DE_REGISTRO];
  const pagina = Number(busca['pagina']);
  return {
    ...(typeof busca['filtro'] === 'string' && filtros.includes(busca['filtro']) ? { filtro: busca['filtro'] as FiltroDaLinha } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
  };
}

/** `?direcao=saida` da aba Lotes. */
export function validarBuscaDosLotes(busca: Record<string, unknown>): { readonly direcao?: 'entrada' | 'saida' } {
  return busca['direcao'] === 'entrada' || busca['direcao'] === 'saida' ? { direcao: busca['direcao'] } : {};
}

/** `?situacao=resolvida` da aba Divergências. */
export function validarBuscaDasDivergencias(busca: Record<string, unknown>): { readonly situacao?: 'aberta' | 'resolvida' } {
  return busca['situacao'] === 'aberta' || busca['situacao'] === 'resolvida' ? { situacao: busca['situacao'] } : {};
}

/**
 * `?vista=a_receber&titulo=<id>` da aba Títulos e notas (0109): o aviso do
 * título vencido abre a vista dele com o título no painel. O celular lê o
 * mesmo par.
 */
export function validarBuscaDosTitulos(busca: Record<string, unknown>): { readonly vista?: 'a_pagar' | 'a_receber'; readonly titulo?: string } {
  const titulo = busca['titulo'];
  return {
    ...(busca['vista'] === 'a_pagar' || busca['vista'] === 'a_receber' ? { vista: busca['vista'] } : {}),
    ...(typeof titulo === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(titulo) ? { titulo } : {}),
  };
}

/* ============================================ títulos e notas (0108) */

export type OrigemDaConta = 'lote' | 'contingencia' | 'plataforma';

/** De onde veio o título, a baixa ou a nota, em selo: nunca um dos seis status da máquina. */
export const ORIGEM_DA_CONTA: Readonly<Record<OrigemDaConta, { readonly rotulo: string; readonly tom: TomDoSelo }>> = {
  lote: { rotulo: 'Do mestre', tom: 'sucesso' },
  contingencia: { rotulo: 'Contingência', tom: 'atencao' },
  plataforma: { rotulo: 'Da plataforma', tom: 'informacao' },
};

export interface TituloDaIntegracao {
  readonly id: string;
  readonly tipo: 'a_pagar' | 'a_receber';
  readonly numero: string;
  readonly contraparte: string | null;
  readonly contraparteCodigo: string | null;
  readonly emitidoEm: string;
  readonly vencimento: string;
  readonly valor: number;
  readonly baixado: number;
  readonly emAberto: number;
  readonly ultimaBaixa: string | null;
  readonly origem: OrigemDaConta;
  readonly sistema: string | null;
  readonly chaveExterna: string | null;
  readonly motivoContingencia: string | null;
  readonly pedido: string | null;
  readonly documento: string | null;
  readonly competencia: string | null;
  readonly canceladoEm: string | null;
  readonly motivoCancelamento: string | null;
}

export interface MestreDaConta {
  readonly linha: boolean;
  readonly mestre: string | null;
}

export interface RespostaDosTitulos {
  readonly titulos: readonly TituloDaIntegracao[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
  readonly totais: {
    readonly aPagarEmAberto: number; readonly aPagarVencido: number; readonly aPagarTitulos: number;
    readonly aReceberEmAberto: number; readonly aReceberVencido: number; readonly aReceberTitulos: number;
  };
  readonly mestres: Readonly<Record<'titulo_a_pagar' | 'titulo_a_receber' | 'baixa' | 'nota_de_entrada' | 'nota_de_saida', MestreDaConta>>;
  readonly hoje: string;
}

export interface BaixaDaIntegracao {
  readonly id: string;
  readonly pagoEm: string;
  readonly valor: number;
  readonly origem: OrigemDaConta;
  readonly chaveExterna: string | null;
  readonly motivoContingencia: string | null;
  readonly canceladaEm: string | null;
  readonly motivoCancelamento: string | null;
}

export interface NotaDaIntegracao {
  readonly id: string;
  readonly tipo: 'entrada' | 'saida';
  readonly numero: string;
  readonly serie: string;
  readonly chaveAcesso: string;
  readonly emitidaEm: string;
  readonly valor: number;
  readonly contraparte: string | null;
  readonly pedido: string | null;
  readonly documento: string | null;
  readonly origem: OrigemDaConta;
  readonly sistema: string | null;
  readonly chaveExterna: string | null;
  readonly motivoContingencia: string | null;
  readonly canceladaEm: string | null;
}

export interface Contingencia {
  readonly id: string;
  readonly entidade: Entidade;
  readonly tabela: string;
  readonly registroId: string;
  readonly operacao: 'inclusao' | 'alteracao';
  readonly motivo: string;
  readonly feitaPor: string | null;
  readonly feitaEm: string;
  readonly mestre: string;
  readonly casadaEm: string | null;
  readonly casadaPeloLote: string | null;
  readonly referencia: string | null;
}

/** A frase de quem é o mestre do título: decide se a plataforma lança ou só em contingência. */
export function quemLancaOTitulo(m: MestreDaConta | undefined, rotulo: string): { readonly externo: boolean; readonly frase: string } {
  if (!m?.mestre) {
    return {
      externo: false,
      frase: m?.linha
        ? `A plataforma é a mestre dos ${rotulo}: o lançamento é feito aqui.`
        : `A matriz não tem linha para os ${rotulo}: a plataforma lança até um mestre ser aprovado.`,
    };
  }
  return { externo: true, frase: `O mestre dos ${rotulo} é ${m.mestre}: eles chegam pela importação. Aqui, só em contingência, com o motivo, e o lote seguinte casa.` };
}

/** "44 dígitos em grupos de 4": a chave de acesso legível na tela. */
export function chaveEmGrupos(chave: string): string {
  return chave.replace(/(\d{4})(?=\d)/g, '$1 ');
}

/** O vencimento escrito: "vence em 20/10/2026" ou "venceu há 3 dias". */
export function vencimentoEscrito(vencimento: string, hoje: string, emAberto: number): string {
  if (emAberto <= 0) return `venceu em ${formatarDia(vencimento)}`;
  const dias = Math.round((Date.parse(`${hoje}T00:00:00Z`) - Date.parse(`${vencimento}T00:00:00Z`)) / 86_400_000);
  if (dias > 0) return `venceu há ${dias === 1 ? '1 dia' : `${formatarInteiro(dias)} dias`}`;
  return `vence em ${formatarDia(vencimento)}`;
}

export interface RespostaDoTitulo {
  readonly id: string;
  readonly tipo: 'a_pagar' | 'a_receber';
  readonly numero: string;
  readonly origem: OrigemDaConta;
  readonly valor: number;
  readonly vencimento: string;
  readonly contraparte: string | null;
}

export function retornoDoTitulo(r: RespostaDoTitulo): DetalheDoRetorno {
  return {
    ...(r.origem === 'contingencia' ? { tom: 'ressalva' as const } : {}),
    alvo: { codigo: r.numero, nome: r.contraparte ?? '' },
    fatos: [formatarReais(r.valor), `vence em ${formatarDia(r.vencimento)}`, r.tipo === 'a_pagar' ? 'a pagar' : 'a receber'],
    efeito: r.origem === 'contingencia'
      ? 'Em contingência: o lote seguinte do mestre casa este título'
      : 'Entra na posição de títulos e no caixa do orçamento',
  };
}

export interface RespostaDaBaixa {
  readonly numero: string;
  readonly origem: OrigemDaConta;
  readonly valor: number;
  readonly pagoEm: string;
  readonly emAbertoAntes: number;
  readonly emAbertoDepois: number;
}

export function retornoDaBaixa(r: RespostaDaBaixa): DetalheDoRetorno {
  return {
    ...(r.origem === 'contingencia' ? { tom: 'ressalva' as const } : {}),
    alvo: { codigo: r.numero },
    fatos: [formatarReais(r.valor), `em ${formatarDia(r.pagoEm)}`, r.origem === 'contingencia' ? 'em contingência' : null],
    efeito: { rotulo: 'Em aberto', antes: formatarReais(r.emAbertoAntes), depois: formatarReais(r.emAbertoDepois) },
  };
}

export function retornoDoCancelamentoDoTitulo(r: { readonly numero: string }, motivo: string): DetalheDoRetorno {
  return { alvo: { codigo: r.numero }, fatos: [`motivo: ${motivo.trim()}`], efeito: { rotulo: 'Situação do título', antes: 'Vivo', depois: 'Cancelado' } };
}

export function retornoDaNota(r: { readonly numero: string; readonly chaveAcesso: string; readonly valor: number; readonly tipo: 'entrada' | 'saida' }): DetalheDoRetorno {
  return {
    tom: 'ressalva',
    alvo: { codigo: r.numero, nome: r.tipo === 'entrada' ? 'Nota de entrada' : 'Nota de saída' },
    fatos: [formatarReais(r.valor), `chave ${r.chaveAcesso.slice(0, 4)}…${r.chaveAcesso.slice(-4)}`],
    efeito: 'Só a referência, em contingência: a plataforma não emite nota; o lote do mestre casa pela chave de acesso',
  };
}

export interface RespostaDaSaidaDeRegistro {
  readonly loteId?: string;
  readonly entidade: EntidadeDeSaida;
  readonly competencia: string;
  readonly revisao?: number;
  readonly semMudanca?: boolean;
  readonly nomeArquivo?: string;
  readonly registros?: number;
  readonly linhas?: number;
  readonly resumo?: { readonly novos: number; readonly alterados: number; readonly cancelados: number; readonly iguais: number };
}

export function tituloDaSaidaDeRegistro(r: RespostaDaSaidaDeRegistro): string {
  return r.semMudanca ? 'Saída sem mudança' : 'Saída gerada';
}

export function retornoDaSaidaDeRegistro(r: RespostaDaSaidaDeRegistro): DetalheDoRetorno {
  if (r.semMudanca || !r.loteId) {
    return { tom: 'ressalva', alvo: { nome: ROTULO_DA_SAIDA[r.entidade] }, fatos: ['nada mudou desde a última saída'], efeito: 'Nenhum lote novo' };
  }
  const x = r.resumo;
  return {
    alvo: { codigo: r.nomeArquivo ?? '', nome: ROTULO_DA_SAIDA[r.entidade] },
    fatos: [
      `${formatarInteiro(r.registros ?? 0)} registros, revisão ${formatarInteiro(r.revisao ?? 1)}`,
      x ? `${formatarInteiro(x.novos)} novos, ${formatarInteiro(x.alterados)} alterados, ${formatarInteiro(x.cancelados)} cancelados` : null,
    ],
    efeito: 'Baixe o arquivo e envie ao sistema de fora; o retorno traz a chave dele',
    ver: `/cadastros/integracoes/lotes/${r.loteId}`,
  };
}
